import { useLayoutEffect, useRef, useState } from 'react';
import { Typography, type TypographyProps } from '@mui/material';

interface AutoFitTextProps extends Omit<TypographyProps, 'children' | 'noWrap'> {
    children: string;
    /** Starting font size in px. */
    maxFontSize: number;
    /** Floor font size in px. */
    minFontSize?: number;
}

/**
 * Single-line text that shrinks its font size to fit the container width
 * instead of truncating with an ellipsis.
 */
export const AutoFitText = ({
    children,
    maxFontSize,
    minFontSize = 10,
    sx,
    ...typographyProps
}: AutoFitTextProps) => {
    const ref = useRef<HTMLElement>(null);
    const [fontSize, setFontSize] = useState(maxFontSize);

    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;

        const fit = () => {
            let size = maxFontSize;
            el.style.fontSize = `${size}px`;

            while (size > minFontSize && el.scrollWidth > el.clientWidth + 0.5) {
                size -= 0.5;
                el.style.fontSize = `${size}px`;
            }
            setFontSize(size);
        };

        fit();

        const observer = new ResizeObserver(() => fit());
        observer.observe(el);
        if (el.parentElement) observer.observe(el.parentElement);

        return () => observer.disconnect();
    }, [children, maxFontSize, minFontSize]);

    return (
        <Typography
            ref={ref}
            noWrap
            {...typographyProps}
            sx={[
                {
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                    flexShrink: 1,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    fontSize: `${fontSize}px`,
                },
                ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
            ]}
        >
            {children}
        </Typography>
    );
};
